import type { FieldError, Problem } from './schema';

export type { FieldError, Problem };

/**
 * Every failed call becomes one ApiError: an RFC 9457 problem from the API,
 * a network failure (status 0, code "network") or an unexpected response.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly detail: string | undefined;
  readonly fieldErrors: readonly FieldError[];
  readonly params: Readonly<Record<string, unknown>>;
  /** The request id (X-Request-ID) for support. */
  readonly requestId: string | undefined;
  /** Seconds from Retry-After on 429/503. */
  readonly retryAfter: number | undefined;

  constructor(init: {
    status: number;
    code: string;
    title?: string;
    detail?: string;
    fieldErrors?: readonly FieldError[];
    params?: Readonly<Record<string, unknown>>;
    requestId?: string;
    retryAfter?: number;
    cause?: unknown;
  }) {
    super(init.detail ?? init.title ?? init.code, { cause: init.cause });
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.title = init.title ?? '';
    this.detail = init.detail;
    this.fieldErrors = init.fieldErrors ?? [];
    this.params = init.params ?? {};
    this.requestId = init.requestId;
    this.retryAfter = init.retryAfter;
  }

  is(...codes: string[]): boolean {
    return codes.includes(this.code);
  }

  /** The first error for a dotted field path ("customer.phone"). */
  field(path: string): FieldError | undefined {
    return this.fieldErrors.find((f) => f.field === path);
  }

  /** Field errors keyed by path, for forms. */
  fieldMap(): Record<string, FieldError> {
    const out: Record<string, FieldError> = {};
    for (const f of this.fieldErrors) if (!(f.field in out)) out[f.field] = f;
    return out;
  }

  get isNetwork(): boolean {
    return this.status === 0;
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
  /** 412: someone else changed the record (If-Match failed). */
  get isVersionConflict(): boolean {
    return this.status === 412 || this.code === 'version_conflict';
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function asFieldErrors(v: unknown): FieldError[] {
  if (!Array.isArray(v)) return [];
  const out: FieldError[] = [];
  for (const item of v) {
    if (isRecord(item) && typeof item.field === 'string' && typeof item.code === 'string') {
      const fe: FieldError = { field: item.field, code: item.code };
      if (typeof item.detail === 'string') fe.detail = item.detail;
      if (isRecord(item.params)) fe.params = item.params;
      out.push(fe);
    }
  }
  return out;
}

/** Builds an ApiError from a failed response and its parsed body (if any). */
export function toApiError(response: Response, body: unknown): ApiError {
  const retry = response.headers.get('Retry-After');
  const requestId = response.headers.get('X-Request-ID') ?? undefined;
  const retryAfter = retry && /^\d+$/.test(retry) ? Number(retry) : undefined;
  if (isRecord(body) && typeof body.code === 'string') {
    return new ApiError({
      status: typeof body.status === 'number' ? body.status : response.status,
      code: body.code,
      title: typeof body.title === 'string' ? body.title : undefined,
      detail: typeof body.detail === 'string' ? body.detail : undefined,
      fieldErrors: asFieldErrors(body.errors),
      params: isRecord(body.params) ? body.params : undefined,
      requestId: typeof body.instance === 'string' ? body.instance : requestId,
      retryAfter,
    });
  }
  const code =
    response.status === 401
      ? 'unauthorized'
      : response.status === 403
        ? 'forbidden'
        : response.status === 404
          ? 'not_found'
          : response.status === 412
            ? 'version_conflict'
            : response.status === 429
              ? 'rate_limited'
              : response.status >= 500
                ? 'internal'
                : 'bad_request';
  return new ApiError({ status: response.status, code, requestId, retryAfter });
}

export function networkError(cause: unknown): ApiError {
  return new ApiError({ status: 0, code: 'network', detail: 'network request failed', cause });
}
